import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { X, MessageSquare, Inbox, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = 'lastInboxPopupSeenAt';

const InboxPopup = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [inboxData, setInboxData] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const checkInbox = useCallback(async () => {
    if (!user) return;

    try {
      const lastSeenAt = localStorage.getItem(STORAGE_KEY);
      const params = lastSeenAt ? { since: lastSeenAt } : {};
      
      const response = await axios.get(`${API}/inbox/status`, {
        headers: getAuthHeaders(),
        params
      });

      const data = response.data;
      
      if (data.hasUnread && data.unreadCount > 0) {
        setInboxData(data);
        setIsAnimating(true);
        // Small delay for animation
        setTimeout(() => setIsVisible(true), 100);
      }
    } catch (error) {
      console.error('Failed to check inbox:', error);
    }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    // Check inbox on mount
    checkInbox();
  }, [checkInbox]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Update last seen timestamp
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleOpenInbox = () => {
    // Update last seen timestamp
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setIsVisible(false);
    
    // Navigate to first conversation if available
    if (inboxData?.conversations?.length > 0) {
      navigate(`/conversations/${inboxData.conversations[0].conversationId}`);
    }
  };

  const handleLater = () => {
    handleDismiss();
  };

  if (!isAnimating || !inboxData) return null;

  return (
    <div 
      className={`fixed bottom-6 right-4 sm:right-6 z-50 transition-all duration-300 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-4 scale-95'
      }`}
      data-testid="inbox-popup"
    >
      <div className="bg-card border shadow-2xl rounded-2xl p-4 sm:p-5 w-[calc(100vw-2rem)] sm:w-[340px] max-w-[340px]">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted transition-colors"
          data-testid="inbox-popup-close"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Header with icon */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl shrink-0">
            <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="pr-6">
            <h3 className="font-semibold text-base flex items-center gap-2">
              New message in your inbox
              <span className="text-lg">💬</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {inboxData.unreadCount === 1 
                ? "There's a new message waiting in your Planner Inbox."
                : `You have ${inboxData.unreadCount} new messages waiting.`}
            </p>
          </div>
        </div>

        {/* Preview of conversations */}
        {inboxData.conversations?.length > 0 && (
          <div className="mb-4 space-y-2">
            {inboxData.conversations.slice(0, 2).map((conv, idx) => (
              <div 
                key={idx}
                className="p-2.5 bg-muted/50 rounded-lg text-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-xs truncate max-w-[180px]">
                    {conv.otherPlannerName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {conv.holdDay}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {conv.preview}
                </p>
              </div>
            ))}
            {inboxData.unreadCount > 2 && (
              <p className="text-xs text-muted-foreground text-center">
                +{inboxData.unreadCount - 2} more
              </p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleOpenInbox}
            className="flex-1 rounded-xl"
            data-testid="inbox-popup-open"
          >
            <Inbox className="h-4 w-4 mr-2" />
            Open Inbox
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            onClick={handleLater}
            className="rounded-xl px-4"
            data-testid="inbox-popup-later"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InboxPopup;
